import React, { useEffect, useState } from "react";
import { LocalStorageUser } from "../../interfaces/user.interface";
import authService from "../../services/auth.service";

export const AuthContext = React.createContext<{
  user: LocalStorageUser | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  authLoading: boolean;
  setAuthLoading: React.Dispatch<React.SetStateAction<boolean>> | null;
}>({
  user: null,
  login: async () => {},
  logout: () => {},
  authLoading: false,
  setAuthLoading: null,
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<LocalStorageUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  const logout = () => {
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        const userInfo = authService.getUserInfo();
        const token = authService.getToken();

        if (!token || !userInfo?._id) {
          setUser(null);
          return;
        }

        const response = await authService.checkToken(token, userInfo._id);

        if (response?.success === true && response?.token) {
          setUser(userInfo);
          authService.setToken(response.token);
        } else {
          logout();
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        logout();
      } finally {
        setAuthLoading(false);
      }
    };

    initAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        login: async (username: string, password: string) => {
          setAuthLoading(true);

          try {
            const response = await authService.adminLogin(username, password);

            if (
              response?.success === true &&
              response?.token &&
              response?.user
            ) {
              setUser(response.user);
              authService.setToken(response.token);
              authService.setUserInfo(response.user);
            } else {
              logout();
            }
          } catch (error) {
            console.error("Login failed:", error);
            logout();
            throw error;
          } finally {
            setAuthLoading(false);
          }
        },
        logout,
        authLoading,
        setAuthLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
